// src/ui/components/elementUI/CompleteDashboardPanel.tsx
import React, { useEffect, useState } from 'react';
import { Wod } from '../../../core/models/Wod';
import { LiveSession } from '../../../core/hooks/useLiveSession';
import WarmUp from '../items/block/WarmUp';
import BlocksList from '../items/block/BlockList';
import { useWodFiltering } from '../../../core/hooks/useWodFiltering';
import { fetchCategoryWods } from '../../../core/services/wodsService';
import { useScreenResolution } from '../../../core/hooks/useScreenResolution';

interface CompleteDashboardPanelProps {
  wod: Wod[];
  liveSession: LiveSession | null;
  boxId: number;
}

const CompleteDashboardPanel: React.FC<CompleteDashboardPanelProps> = ({
  wod,
  liveSession,
  boxId,
}) => {
  // CompleteDashboardPanel NUNCA muestra WODs de competidores en el listado
  // Los WODs de competidores solo aparecen en FullscreenDialog individual
  const { filteredWods } = useWodFiltering(wod, boxId, {
    includeCompetitors: false, // NUNCA mostrar competidores en el listado principal
    requireContent: true, // Solo mostrar WODs con contenido
  });
  const screenResolution = useScreenResolution();

  // Estado para categorías
  const [categoryState, setCategoryState] = useState<any[]>([]);
  const [catsLoading, setCatsLoading] = useState(false);

  const expanded = liveSession?.expanded;
  const fullscreen = liveSession?.fullscreen;

  // Cargar categorías
  useEffect(() => {
    let mounted = true;
    async function loadCats() {
      setCatsLoading(true);
      try {
        const listCategoryWods = await fetchCategoryWods(Number(boxId));
        if (!mounted) return;
        setCategoryState(Array.isArray(listCategoryWods) ? listCategoryWods : []);
      } catch (e) {
        console.warn('No se pudieron cargar category wods', e);
        if (mounted) setCategoryState([]);
      } finally {
        if (mounted) setCatsLoading(false);
      }
    }
    loadCats();
    return () => {
      mounted = false;
    };
  }, [boxId]);

  // Función para obtener el título del WOD con categoría
  const getWodTitle = (singleWod: any) => {
    if (catsLoading) return singleWod.title || `WOD ${singleWod.id}`;

    const category = categoryState.find((cat) => cat.id === Number(singleWod.type_id));
    if (category && category.name) {
      return `${category.name}: ${singleWod.title}`;
    }
    return singleWod.title || `WOD ${singleWod.id}`;
  };

  // Determinar layout: 2 columnas si hay 2+ WODs y es pantalla grande
  const shouldUseTwoColumns = filteredWods.length >= 2 && screenResolution.isLargeScreen;

  return (
    <div className="min-w-[0] flex-1 bg-transparent overflow-y-auto p-2 custom-scrollbar">
      <div
        className={`grid gap-4 h-fit ${shouldUseTwoColumns ? 'grid-cols-2' : 'grid-cols-1'
          }`}
      >
        {filteredWods.map((singleWod) => (
          <div
            key={singleWod.id}
            className="flex flex-col space-y-3 bg-gray-900/20 rounded-[24px] p-3"
          >
            {/* NOMBRE DEL WOD */}
            <div className="text-center border-b border-gray-700 pb-2">
              <h2 className="tv-title-wod font-bold text-white">{getWodTitle(singleWod)}</h2>
              {/* <p className="text-gray-400 text-sm mt-2">
                {new Date(singleWod.date).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p> */}
            </div>

            {/* WARM-UP (si existe) - Manteniendo lógica de WarmupOrBlockViewer */}
            {singleWod.warm_up && singleWod.warm_up !== '' && (
              <div className="space-y-2">
                <div className="pl-2 border-l-2 border-orange-400/30">
                  <WarmUp
                    wod={singleWod}
                    html={singleWod.warm_up}
                    showExpanded={singleWod.id === liveSession?.elementId && expanded}
                    showFullScreen={fullscreen && liveSession?.elementId === singleWod.id}
                  />
                </div>
              </div>
            )}

            {/* BLOQUES - Manteniendo lógica de BlocksList */}
            {Array.isArray(singleWod.blocks) && singleWod.blocks.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <h3 className="tv-text-xl font-semibold text-blue-400">Bloques</h3>
                </div>
                {/* 🔍 DEBUG: Log de bloques antes de pasar a BlocksList */}
                {(() => {
                  console.log('[CompleteDashboardPanel DEBUG] Bloques antes de BlocksList:', {
                    wodId: singleWod.id,
                    bloquesCount: singleWod.blocks.length,
                    bloques: singleWod.blocks.map((b) => ({
                      id: b.id,
                      title: b.title,
                      work: b.work ? (b.work.length > 50 ? b.work.substring(0, 50) + '...' : b.work) : 'VACÍO',
                      is_deleted: b.is_deleted,
                    })),
                  });
                  return null;
                })()}
                <div className="pl-2 border-l-2 border-blue-400/30">
                  <BlocksList
                    wod={wod}
                    liveSession={liveSession}
                    boxId={boxId}
                    filterToSingleWod={singleWod.id}
                  />
                </div>
              </div>
            )}

            {/* Mensaje si no hay contenido */}
            {(!singleWod.warm_up || singleWod.warm_up === '') &&
              (!Array.isArray(singleWod.blocks) || singleWod.blocks.length === 0) && (
                <div className="text-center text-gray-500 py-4">
                  <p>No hay contenido disponible para este WOD</p>
                </div>
              )}
          </div>
        ))}


        {/* Mensaje si no hay WODs */}
        {filteredWods.length === 0 && (
          <div className="col-span-full text-center text-gray-500 py-12">
            <h3 className="text-xl font-semibold mb-2">No hay WODs disponibles</h3>
            <p>No se encontraron entrenamientos para mostrar.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(CompleteDashboardPanel);
